import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react'
import AGGrid from "../../../Components/AGGrid";
import { CommonUtil } from "../../../utils/CommonUtil";
import { NetworkManager } from "../../../Components/NetworkManager";
import { Constants } from "../../Languages/Constants";
import Offcanvas from "../../../Components/OffCanvas";
import GenericPopup from "../../../Components/GenericPopup";
import ToastManager from '../../../Components/ToastManager';
import HistoryViewer from './HistoryViewer';
import InvoiceDetailViewer from './InvoiceDetailViewer';
import { Events } from '../../Languages/Events';
import FilterButtonGroup, { FilterButtonOption } from '../../../Components/FilterButtonGroup';

type ComplianceFilterState = {
    all: boolean;
    compliant: boolean;
    nonCompliant: boolean;
    deferred: boolean;
};

const PendingInvoices = () => {
    const gridRef = useRef<any>(null);
    const [rowsData, setRowsData] = useState<any>([]);
    const [showPopup, setShowPopup] = useState(false);
    const [selectedRowData, setSelectedRowData] = useState<any>(null);
    const [groupDetails, setGroupDetails] = useState<any>(null);
    const [activeTab, setActiveTab] = useState('invoice');
    const [showHistory, setShowHistory] = useState(false);
    const [historyData, setHistoryData] = useState<any>([]);
    const [selectedGroupCodes, setSelectedGroupCodes] = useState<string[]>([]);
    const [complianceFilters, setComplianceFilters] = useState<ComplianceFilterState>({
        all: true,
        compliant: false,
        nonCompliant: false,
        deferred: false
    });

    const complianceFilterOptions: FilterButtonOption<keyof ComplianceFilterState>[] = useMemo(() => ([
        { key: 'all', label: 'All' },
        { key: 'compliant', label: 'Valid Invoices' },
        { key: 'nonCompliant', label: 'Invoices with Errors' },
        { key: 'deferred', label: 'Deferred Invoices' }
    ]), []);

    const getFilterButtonStyle = (isActive: boolean) => {
        const baseStyle: React.CSSProperties = {
            border: '1px solid #e4e4e4',
            borderRadius: '15px',
            marginTop: '5px',
            marginRight: '8px',
            color: 'black',
            boxShadow: '1px 2px #e4e4e4',
            width: 'auto',
            minWidth: '155px',
            padding: '0 14px',
            whiteSpace: 'nowrap'
        };

        if (isActive) {
            return {
                ...baseStyle,
                background: '#23D2C1',
                boxShadow: 'none'
            };
        }

        return {
            ...baseStyle,
            background: 'white',
            boxShadow: '1px 2px #e4e4e4'
        };
    };

    const filteredRowsData = useMemo(() => {
        if (complianceFilters.all) {
            return rowsData;
        }

        if (complianceFilters.deferred) {
            return rowsData.filter((row: any) => row?.isDeferred === true);
        }

        const pendingRows = rowsData.filter((row: any) => row?.isDeferred !== true);

        if (complianceFilters.compliant) {
            return pendingRows.filter((row: any) => row?.isFlagged === false);
        }

        if (complianceFilters.nonCompliant) {
            return pendingRows.filter((row: any) => row?.isFlagged === true);
        }

        return pendingRows;
    }, [rowsData, complianceFilters]);

    const fetchGroupDetails = useCallback((groupCode: string) => {
        NetworkManager.sendJsonRequest(`api/prextra/review/group/${groupCode}`, '', '', 'GET', true, (data: any) => {
            if (data) {
                const parsedData = JSON.parse(data);
                setGroupDetails(parsedData);
                setShowPopup(true);
            }
        }, (error: any) => {
            console.error(error);
        });
    }, [])

    const handleViewHistory = (tab: string, id: any) => {
        setActiveTab(tab);
        NetworkManager.sendJsonRequest(`api/prextra/review/history/pending/${id}`, '', '', 'GET', true, (data: any) => {
            if (data) {
                setHistoryData(JSON.parse(data));
                setShowHistory(true);
            }
        }, (error: any) => {
            console.error(error);
        });
    }

    const invoiceButtonRender = useCallback((propData: any) => {
        const onClickInvoice = () => {
            setSelectedRowData(propData.data);
            fetchGroupDetails(propData.data.groupCode);
        }
        return (
            <button className="btn btn-primary btn-sm mb-1" style={{ cursor: 'pointer', minWidth: '200px' }} onClick={onClickInvoice}>{propData.data.groupCode}</button>
        )
    }, [fetchGroupDetails])

    const isRowSelectable = (rowNode: any) => {
        if (complianceFilters.deferred) {
            return true;
        }
        return rowNode.data ? !rowNode.data.isFlagged : true;
    }

    const pendingInvoicesCol: any = useMemo(() => {
        const columns: any[] = [
            {
                headerCheckboxSelection: true,
                headerCheckboxSelectionFilteredOnly: true,
                checkboxSelection: true,
                width: 70,
                maxWidth: 100,
                resizable: false,
                sortable: false,
                filter: false,
                suppressMenu: true,
                cellClass: 'ag-checkbox-center',
                headerClass: 'ag-checkbox-center'
            },
            {
                field: 'button',
                headerName: "Invoices",
                resizable: true,
                sortable: true,
                flex: 1,
                filter: 'agNumberColumnFilter',
                valueGetter: (params: any) => params?.data?.groupCode || '',
                cellRenderer: invoiceButtonRender,
            },
            { field: 'reservationCode', headerName: CommonUtil.getMessageText(Constants.RESERVATION_CODE, "Reservation Code"), resizable: true, sortable: true, flex: 1, filter: 'agNumberColumnFilter' },
            { field: 'unitNumber', headerName: CommonUtil.getMessageText(Constants.UNIT_NUMBER, "Unit Number"), resizable: true, sortable: true, flex: 1, filter: 'agTextColumnFilter' },
            { field: 'vinNumber', headerName: CommonUtil.getMessageText(Constants.VIN_NUMBER, "VIN Number"), resizable: true, sortable: true, flex: 1, filter: 'agTextColumnFilter' },
            { field: 'invoiceTotalAmount', headerName: "Invoice Total Amount", resizable: true, sortable: true, flex: 1, filter: 'agTextColumnFilter' },
            { field: 'statementTotalAmount', headerName: "Statement Total Amount", resizable: true, sortable: true, flex: 1, filter: 'agTextColumnFilter' },
            { field: 'ownerOrganization', headerName: CommonUtil.getMessageText(Constants.OWNER_ORGANIZATION, "Owner Organization"), resizable: true, sortable: true, flex: 1, filter: 'agNumberColumnFilter' },
            { field: 'renterOrganization', headerName: CommonUtil.getMessageText(Constants.RENTER_ORGANIZATION, "Renter Organization"), resizable: true, sortable: true, flex: 1, filter: 'agNumberColumnFilter' },
        ];

        if (complianceFilters.all) {
            columns.push({
                field: 'isDeferred',
                headerName: 'isDeferred',
                resizable: true,
                sortable: true,
                flex: 1,
                filter: 'agTextColumnFilter',
                valueGetter: (params: any) => {
                    const deferredValue = params?.data?.isDeferred ?? params?.data?.isDeffered;
                    return deferredValue === true ? 'true' : 'false';
                }
            });
        }

        return columns;
    }, [complianceFilters.all, invoiceButtonRender]);

    
    useEffect(() => {
        CommonUtil.SCREEN_NAME = CommonUtil.PENDING_INVOICES;
        document.addEventListener(Events.SWITCH_CUSTOMER_EVENT, onSwitchCustomer);
        
        fetchPendingBills();

        CommonUtil.getAllAssetTypes((data: any) => {
            CommonUtil.ASSET_TYPES = data;
        });

        return () => {
            document.removeEventListener(Events.SWITCH_CUSTOMER_EVENT, onSwitchCustomer);
        };
    }, []);
    
    let onSwitchCustomer = () => {
        Events.dispatchBreadCrumbEvent();
    }

    const fetchPendingBills = () => {
        NetworkManager.sendJsonRequest('api/prextra/review/pendingInvoices', '', '', 'GET', true, (data: any) => {
            if (data) {
                setRowsData(JSON.parse(data));
            }
        }, (error: any) => {
            console.error(error);
        });
    }

    const handleSelectionChanged = () => {
        const selectedRows = gridRef.current?.getSelectedRows();
        const groupCodes = selectedRows ? selectedRows.map((row: any) => row.groupCode) : [];
        setSelectedGroupCodes(groupCodes);
    }

    const clearSelectedInvoices = () => {
        setSelectedGroupCodes([]);
        const gridApi = gridRef.current?.getGridAPI?.();
        gridApi?.deselectAll?.();
    }

    const handleSendToPrextraSelected = () => {
        if (selectedGroupCodes.length === 0) return;

        const payload = {
            groupCodes: selectedGroupCodes
        };

        NetworkManager.sendJsonRequest('api/prextra/review/sendToPrextra', payload, '', 'POST', true, (res: any) => {
            if (res) {
                const response = JSON.parse(res);
                if (!response.errorMessage) {
                    ToastManager.showToast("Sent to Prextra Successfully", 'Success');
                    fetchPendingBills();
                } else {
                    ToastManager.showToast(response.errorMessage, 'Error');
                }
            }
        }, (err: any) => {
            console.error(err);
        });
    }

    const handleDeferInvoices = (deferred: boolean) => {
        if (selectedGroupCodes.length === 0) return;

        const payload: any = {
            groupCodes: selectedGroupCodes,
            deferred
        };

        NetworkManager.sendJsonRequest('api/prextra/review/deferGroups', payload, '', 'POST', true, (res: any) => {
            if (res) {
                const response = JSON.parse(res);
                if (!response.errorMessage) {
                    ToastManager.showToast(deferred ? 'Invoices deferred successfully' : 'Invoices un-deferred successfully', 'Success');
                    clearSelectedInvoices();
                    fetchPendingBills();
                } else {
                    ToastManager.showToast(response.errorMessage, 'Error');
                }
            }
        }, (err: any) => {
            // Keep current selection untouched when defer API returns errors.
            console.error(err);
        });
    }

    const handleComplianceFilterChange = (nextSelection: ComplianceFilterState) => {
        clearSelectedInvoices();
        setComplianceFilters(nextSelection);
    };

    return (
        <div className="container-fluid d-flex flex-column" style={{ height: "calc(100vh - 90px)", overflow: "hidden", paddingTop: "0.2rem", paddingBottom: 0 }}>
            <FilterButtonGroup<keyof ComplianceFilterState>
                options={complianceFilterOptions}
                selectedMap={complianceFilters}
                mode="single"
                onSelectionChange={handleComplianceFilterChange}
                getButtonStyle={(isActive) => getFilterButtonStyle(isActive)}
                buttonClassName="btn button-height"
                containerClassName="row mx-1 mb-1"
                containerStyle={{ paddingTop: "10px", paddingBottom: "10px" }}
            />
            <div className="ag-theme-alpine" style={{ flex: 1, minHeight: 0, width: "100%" }}>
                <AGGrid
                    id="PendingInvoicesGrid"
                    cols={pendingInvoicesCol}
                    rows={filteredRowsData}
                    pageSize={500}
                    headerHeight={CommonUtil.GRID_HEADER_HEIGHT}
                    rowHeight={CommonUtil.GRID_ROW_HEIGHT}
                    ref={gridRef}
                    addNew={false}
                    rowSelection="multiple"
                    suppressRowClickSelection={true}
                    isRowSelectable={isRowSelectable}
                    onSelectionChanged={handleSelectionChanged}
                    getRowId={(params: any) => params.data.groupCode}
                    getRowStyle={(params: any) => {
                        if (params.data?.isFlagged) {
                            return {
                                backgroundColor: '#f8d7da',
                                color: '#721c24'
                            };
                        }
                        return {};
                    }}
                />
            </div>
            <div className="d-flex justify-content-end my-3" style={{ paddingTop: 0, paddingBottom: 0, marginBottom: 0, flexShrink: 0 }}>
                <button
                    className="btn btn-secondary me-3"
                    style={{ marginTop: 0, marginBottom: 0 }}
                    onClick={() => handleDeferInvoices(!complianceFilters.deferred)}
                    disabled={selectedGroupCodes.length === 0}
                >
                    {complianceFilters.deferred ? 'Un-Defer Invoices' : 'Defer Invoices'}
                </button>
                <button 
                    className="btn btn-primary me-3" 
                    style={{ marginTop: 0, marginBottom: 0 }}
                    onClick={handleSendToPrextraSelected}
                    disabled={selectedGroupCodes.length === 0}
                >
                    Send To Prextra
                </button>
            </div>

            <Offcanvas
                show={showPopup}
                onHide={() => {setShowPopup(false); fetchPendingBills();}}
                title={`Invoice Details - ${selectedRowData?.groupCode || ''}`}
                size="90%"
                placement="end"
                scrollable={false}
            >
                {showPopup && (
                    <InvoiceDetailViewer
                        groupDetails={groupDetails}
                        groupCode={selectedRowData?.groupCode}
                        onRefresh={fetchGroupDetails}
                        onViewHistory={handleViewHistory}
                        onClose={() => {setShowPopup(false); fetchPendingBills();}}
                    />
                )}
            </Offcanvas>
            <GenericPopup
                header={`Review ${activeTab} history - ${selectedRowData?.groupCode || ''}`}
                mode={showHistory ? "view" : null}
                onClose={() => setShowHistory(false)}
                fullscreen={true}
            >
                <HistoryViewer historyData={historyData} onClose={() => setShowHistory(false)} />
            </GenericPopup>
        </div>
    )
}

export default PendingInvoices